import chroma from 'chroma-js'
import { getColorMap } from '~/design'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import { Info } from './statsAndGeoToFeatureCollection.util'
import { isVariableLayer } from './clusterListInput.util'

export type ValueType = 'count' | 'percentage' | 'percentageCustom' | 'hidden' | 'money'

export interface StyleForLayer {
  mapStyle: (info: { value?: number }) => {
    fillColor: string
    strokeColor: string
    strokeWeight: number
    fillOpacity: number
    strokeOpacity?: number
    zIndex: number
  }
  valueType: ValueType
  colorMap: string[]
  min: number
  max: number
  total: number
  totalNumerator: number
  totalDenominator: number
  outsideSelectionTotal: number
  outsideSelectionTotalNumerator: number
  outsideSelectionTotalDenominator: number
}

export default function styleForLayer(
  layer: Layer,
  {
    minValue,
    maxValue,
    totalValue,
    totalNumeratorValue,
    totalDenominatorValue,
    outsideSelectionTotalValue,
    outsideSelectionTotalNumeratorValue,
    outsideSelectionTotalDenominatorValue,
    fillOpacity = 0.7,
    strokeOpacity = 1,
  }: {
    minValue: number
    maxValue: number
    totalValue?: number
    totalNumeratorValue?: number
    totalDenominatorValue?: number
    outsideSelectionTotalValue?: number
    outsideSelectionTotalNumeratorValue?: number
    outsideSelectionTotalDenominatorValue?: number
    fillOpacity?: number
    strokeOpacity?: number
  },
): StyleForLayer {
  const valueType: ValueType = isVariableLayer(layer) ? (layer.variable.unitFormat ?? 'count') : 'count'

  let min: number = minValue
  let max: number = maxValue
  let total: number = totalValue ?? 0
  let totalNumerator: number = totalNumeratorValue ?? 0
  let totalDenominator: number = totalDenominatorValue ?? 0
  let outsideSelectionTotal: number = outsideSelectionTotalValue ?? 0
  let outsideSelectionTotalNumerator: number = outsideSelectionTotalNumeratorValue ?? 0
  let outsideSelectionTotalDenominator: number = outsideSelectionTotalDenominatorValue ?? 0

  // Se for percentual, a legenda deve sempre mostrar 0-100%
  if (valueType === 'percentage') {
    min = 0
    max = 100
  }

  const colorMap =
    layer.space.colorMap ??
    getColorMap(isVariableLayer(layer) ? (layer.variable.betterDensity ?? 'less') : 'less', 'choropleth')

  const colorMapFunction = chroma.scale(colorMap).mode('lrgb')

  const isHeatmap =
    layer.space.childrenType === 'item' && layer.space.childrenItemView === 'heatmap'

  const mapStyle = ({ value }: Partial<Info>) => {
    if (value === undefined) {
      return borderOnlyStyle.mapStyle()
    }

    const ratio = max === min ? (min ? 1 : 0) : (value - min) / (max - min)
    const chromaColor = colorMapFunction(ratio)
    const fillColor = isHeatmap ? 'RGBA(0,0,0,0)' : chromaColor.hex()
    const strokeColor = isHeatmap ? 'RGBA(0,0,0,0)' : chromaColor.darken().hex()

    const zIndex = Math.round(ratio * 100)

    return {
      fillColor,
      strokeColor,
      strokeWeight: 2,
      strokeOpacity,
      fillOpacity,
      zIndex,
    }
  }

  return {
    mapStyle,
    valueType,
    colorMap,
    min,
    max,
    total,
    totalNumerator,
    totalDenominator,
    outsideSelectionTotal,
    outsideSelectionTotalNumerator,
    outsideSelectionTotalDenominator,
  }
}

export const borderOnlyStyle = {
  mapStyle: () => ({
    fillColor: '#000000',
    fillOpacity: 0,
    strokeColor: '#FF6900',
    strokeWeight: 2,
    strokeOpacity: 0.8,
    zIndex: 0,
  }),
  colorMap: ['#FF6900'],
  valueType: 'count' as ValueType,
  min: 0,
  max: 0,
  total: 0,
  totalNumerator: 0,
  totalDenominator: 0,
  outsideSelectionTotal: 0,
  outsideSelectionTotalNumerator: 0,
  outsideSelectionTotalDenominator: 0,
}
